/**
 * localStorage read/write for the docs slider's persisted open state.
 *
 * Requirement 1.5: the boolean open flag and `lastDocPath` are persisted
 * under `ledgiventa.docs-panel.open.${sessionKey}` when `sessionKey` is
 * defined.
 *
 * Requirement 1.6: every read and write is wrapped in `try/catch` so that
 * quota-exceeded, disabled localStorage, private-browsing SecurityError,
 * malformed JSON, or schema mismatch never crash the app. The docs panel
 * silently falls back to session-only state on any failure — no console
 * output, no thrown exception.
 *
 * Never persists chatbot-open state — only Docs_Slider `open` and
 * `lastDocPath` (design § Overview principle 6).
 *
 * @packageDocumentation
 */

import type { DocsPanelPersistedState } from "./types";

/**
 * localStorage key namespace for the docs-slider persisted state.
 *
 * @remarks
 * Full key is `ledgiventa.docs-panel.open.${sessionKey}`. The `sessionKey`
 * is a hashed identifier (SHA-256 of `ownerId`, first 16 hex chars) so the
 * raw `ownerId` never lands in localStorage (Constitution Rule II).
 */
const STORAGE_KEY_PREFIX = "ledgiventa.docs-panel.open.";

/**
 * SSR-safe accessor for `window.localStorage`.
 *
 * Returns `null` when running server-side (Node/SSR), when `window` is
 * undefined, when `window.localStorage` is undefined, or when accessing
 * `localStorage` itself throws (some browser configurations throw
 * `SecurityError` on access, not just on read/write).
 */
function getLocalStorage(): Storage | null {
  try {
    if (typeof window === "undefined") {
      return null;
    }
    if (typeof window.localStorage === "undefined") {
      return null;
    }
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Type-guard for `DocsPanelPersistedState`.
 *
 * Enforces the schema from `types.ts`:
 * - `open` MUST be a boolean
 * - `version` MUST equal the literal `1`
 * - `lastDocPath` MUST be a string when present (optional field)
 *
 * Any extra fields on `value` are ignored (they will not be persisted on
 * subsequent writes because {@link writePersistedState} only serialises the
 * three allowed fields).
 */
function isValidPersistedState(
  value: unknown
): value is DocsPanelPersistedState {
  if (value === null || typeof value !== "object") {
    return false;
  }
  const record = value as Record<string, unknown>;
  if (typeof record.open !== "boolean") {
    return false;
  }
  if (record.version !== 1) {
    return false;
  }
  if (
    record.lastDocPath !== undefined &&
    typeof record.lastDocPath !== "string"
  ) {
    return false;
  }
  return true;
}

/**
 * Read the persisted docs-panel state for the given session key.
 *
 * @param sessionKey - The hashed session key (16-char lowercase hex). When
 *   undefined, no read is attempted and `null` is returned.
 * @returns The parsed state on success, or `null` on any failure (missing
 *   key, malformed JSON, schema mismatch, localStorage unavailable,
 *   SecurityError). Never throws.
 */
export function readPersistedState(
  sessionKey?: string
): DocsPanelPersistedState | null {
  if (sessionKey === undefined) {
    return null;
  }
  const storage = getLocalStorage();
  if (storage === null) {
    return null;
  }
  try {
    const raw = storage.getItem(STORAGE_KEY_PREFIX + sessionKey);
    if (raw === null) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    if (!isValidPersistedState(parsed)) {
      return null;
    }
    // Return a fresh object stripped of any unknown fields — this keeps
    // downstream consumers from accidentally reading extras that were
    // written by a different (or future) version of the schema.
    const clean: DocsPanelPersistedState = {
      open: parsed.open,
      version: 1,
    };
    if (parsed.lastDocPath !== undefined) {
      clean.lastDocPath = parsed.lastDocPath;
    }
    return clean;
  } catch {
    return null;
  }
}

/**
 * Write the docs-panel state under the given session key.
 *
 * @param sessionKey - The hashed session key. When undefined the write is
 *   skipped entirely (session-only state — Requirement 1.5).
 * @param state - The state to persist. Only the fields defined in
 *   `DocsPanelPersistedState` are serialised; any extra fields on the
 *   incoming object are silently dropped.
 *
 * Silently swallows `QuotaExceededError`, `SecurityError`, and any other
 * localStorage exception. Never throws (Requirement 1.6).
 */
export function writePersistedState(
  sessionKey: string | undefined,
  state: DocsPanelPersistedState
): void {
  if (sessionKey === undefined) {
    return;
  }
  const storage = getLocalStorage();
  if (storage === null) {
    return;
  }
  // Build a minimal payload with only the allowed fields so unknown
  // properties from the caller never land on disk.
  const payload: DocsPanelPersistedState = {
    open: state.open,
    version: 1,
  };
  if (state.lastDocPath !== undefined) {
    payload.lastDocPath = state.lastDocPath;
  }
  try {
    storage.setItem(STORAGE_KEY_PREFIX + sessionKey, JSON.stringify(payload));
  } catch {
    // Silently swallow QuotaExceededError, SecurityError, and any other
    // localStorage failure. The docs panel MUST NEVER crash the app because
    // of a storage write failure (Req 1.6).
  }
}
